<template>
  <div class="claim-info claim-category">
    <div class="claim-category-item">
      <label>Claims Category</label>
      <triggerable-dropdown
        name="categoryName"
        v-model="localCategoryId"
        :options="availableClaimCategoryOptions"
      />
    </div>
    <div class="claim-category-item">
      <label>Issue Category</label>
      <triggerable-dropdown
        name="issueCategoryName"
        v-model="localTypeId"
        :options="availableClaimIssueCategoryOptions"
      />
    </div>
    <div class="claim-category-item">
      <label>Issue Type</label>
      <triggerable-dropdown
        name="issueTypeName"
        v-model="localIssueId"
        :options="availableClaimIssueTypeOptions"
      />
    </div>
    <div class="claim-category-item">
      <label>Sub-Issue Type</label>
      <triggerable-dropdown
        name="issueSubIssueName"
        v-model="localSubIssueId"
        :options="availableClaimSubIssueTypeOptions"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { computed, ComputedRef, defineComponent, ref, Ref, watch } from 'vue';
import { debounce } from 'lodash';
import {
  ICategory,
  IClaimTypeSelections,
  IIssueCategory,
  IIssueType,
  ISubIssueType,
} from '@/types';
import { useTaxonomy } from '@/hooks';
import TriggerableDropdown from '@/components/TriggerableDropdown.vue';

export default defineComponent({
  components: {
    TriggerableDropdown,
  },
  name: 'ClaimTypeSelector',
  props: {
    categoryId: {
      type: Number,
      required: false,
      default: null,
    },
    typeId: {
      type: Number,
      required: false,
      default: null,
    },
    issueId: {
      type: Number,
      required: false,
      default: null,
    },
    subIssueId: {
      type: Number,
      required: false,
      default: null,
    },
  },
  emits: ['input'],
  setup(props, { emit }) {
    /**
     * Claims Category (a.k.a., the first dropdown)
     */
    const localCategoryId: Ref<number | null> = ref(props.categoryId);

    /**
     * Issue Category (a.k.a., the second dropdown)
     */
    const localTypeId: Ref<number | null> = ref(props.typeId);

    /**
     * Issue Type (a.k.a., the third dropdown)
     */
    const localIssueId: Ref<number | null> = ref(props.issueId);

    /**
     * Sub-Issue Type (a.k.a, the fourth dropdown)
     */
    const localSubIssueId: Ref<number | null> = ref(props.subIssueId);

    const {
      categories,
      getCategoryTypes,
      getCategoryTypeIssues,
      getCategoryTypeIssuesSubIssues,
    } = useTaxonomy();

    /**
     * Active claims category options, for the values in the first dropdown.
     */
    const availableClaimCategoryOptions: ComputedRef<ICategory[]> = computed<ICategory[]>(
      () => categories.value?.filter(option => option.isActive) || []
    );

    /**
     * Active issue category options, for the values in the second dropdown.
     * These values depend on the selected claim category (from the first dropdown)
     */
    const availableClaimIssueCategoryOptions: ComputedRef<IIssueCategory[]> = computed<
      IIssueCategory[]
    >(() => {
      if (localCategoryId.value !== null) {
        return getCategoryTypes(localCategoryId.value).filter(option => option.isActive);
      } else {
        return [];
      }
    });

    /**
     * Active issue type options, for the values in the third dropdown.
     * These values depend on the selected claim category (from the first dropdown),
     * and the selected claim issue category (from the second dropdown)
     */
    const availableClaimIssueTypeOptions: ComputedRef<IIssueType[]> = computed<IIssueType[]>(() => {
      if (localCategoryId.value !== null && localTypeId.value !== null) {
        return getCategoryTypeIssues(localCategoryId.value, localTypeId.value).filter(
          option => option.isActive
        );
      } else {
        return [];
      }
    });

    /**
     * Active sub issue type options, for the values in the fourth dropdown.
     * These values depend on the selected claim category (from the first dropdown),
     * the selected claim issue category (from the second dropdown)
     * and the selected claim issue type (from the third dropdown)
     */
    const availableClaimSubIssueTypeOptions: ComputedRef<ISubIssueType[]> = computed<
      ISubIssueType[]
    >(() => {
      if (
        localCategoryId.value !== null &&
        localTypeId.value !== null &&
        localIssueId.value !== null
      ) {
        return getCategoryTypeIssuesSubIssues(
          localCategoryId.value,
          localTypeId.value,
          localIssueId.value
        ).filter(option => option.isActive);
      } else {
        return [];
      }
    });

    /**
     * These four values can change in rapid succession (e.g., when `categoryId` changes,
     * it creates a cascade of reactivity that causes `typeId`,`issueId` and `subIssueId` to become `null`.
     * `debounce` lets the reactivity settle, before emitting the correct values.
     */
    watch(
      [localCategoryId, localTypeId, localIssueId, localSubIssueId],
      debounce(() => {
        const newValue: IClaimTypeSelections = {
          categoryId: localCategoryId.value,
          typeId: localTypeId.value,
          issueId: localIssueId.value,
          subIssueId: localSubIssueId.value,
        };
        emit('input', newValue);
      })
    );

    return {
      localCategoryId,
      localTypeId,
      localIssueId,
      localSubIssueId,
      availableClaimCategoryOptions,
      availableClaimIssueCategoryOptions,
      availableClaimIssueTypeOptions,
      availableClaimSubIssueTypeOptions,
    };
  },
});
</script>

<style lang="scss" scoped>
.claim-category {
  display: flex;

  .claim-category-item {
    margin-right: var(--space-2);
    margin-bottom: var(--space-2);
    width: 33%;

    &:last-child {
      width: 34%;
      margin-right: 0;
    }
  }
}
</style>
